<template>
  <div class="show_all_content_wrapper">
    <!-- Start:: Main Section -->
    <main>
      <!--  =========== Start:: Filter Form =========== -->
      <div
        class="filter_content_wrapper"
        :class="{ active: filterFormIsActive }"
      >
        <button
          class="filter_toggler"
          @click="filterFormIsActive = !filterFormIsActive"
        >
          <i class="fal fa-times"></i>
        </button>
        <div class="filter_title_wrapper">
          <h5>{{ $t("TITLES.searchBy") }}</h5>
        </div>
        <div class="filter_form_wrapper">
          <form @submit.prevent="submitFilterForm">
            <div class="row justify-content-center align-items-center w-100">
              <!-- Start:: Search Input -->
              <base-input
                col="6"
                type="text"
                :placeholder="$t('complaints.searchPlaceholder')"
                v-model.trim="filterOptions.q"
              />
              <!-- End:: Search Input -->

              <!-- Start:: Status Select -->
              <base-input
                col="3"
                type="text"
                :placeholder="$t('complaints.bookingIdFilter')"
                v-model.trim="filterOptions.booking_id"
              />
              <base-select-input
                col="3"
                :optionsList="statusOptions"
                :placeholder="$t('complaints.filterByStatus')"
                v-model="filterOptions.status"
              />
              <!-- End:: Status Select -->
            </div>

            <div class="btns_wrapper">
              <button class="submit_btn" :disabled="isWaitingRequest">
                <i class="fal fa-search"></i>
              </button>
              <button
                class="reset_btn"
                type="button"
                :disabled="isWaitingRequest"
                @click="resetFilter"
              >
                <i class="fal fa-redo"></i>
              </button>
            </div>
          </form>
        </div>
      </div>
      <!--  =========== End:: Filter Form =========== -->

      <!--  =========== Start:: Table Title =========== -->
      <div class="table_title_wrapper">
        <div class="title_text_wrapper">
          <h5>{{ $t("complaints.title") }}</h5>
          <button
            v-if="!filterFormIsActive"
            class="filter_toggler"
            @click.stop="filterFormIsActive = !filterFormIsActive"
          >
            <i class="fal fa-search"></i>
          </button>
        </div>
      </div>
      <!--  =========== End:: Table Title =========== -->

      <!--  =========== Start:: Data Table =========== -->
      <v-data-table
        class="thumb"
        :loading="loading"
        :loading-text="$t('complaints.loadingComplaints')"
        :headers="tableHeaders"
        :items="tableRows"
        item-class="ltr"
        :items-per-page="paginations.items_per_page"
        hide-default-footer
      >
        <!-- Start:: No Data State -->
        <template v-slot:no-data>
          {{ $t("complaints.noComplaints") }}
        </template>
        <!-- End:: No Data State -->

        <!-- Start:: Serial Number -->
        <template v-slot:[`item.serialNumber`]="{ item }">
          <p>{{ item.serialNumber }}</p>
        </template>
        <!-- End:: Serial Number -->

        <!-- Start:: Client Name -->
        <template v-slot:[`item.user.name`]="{ item }">
          <p>{{ item.user.name }}</p>
        </template>
        <!-- End:: Client Name -->

        <!-- Start:: Submission Date -->
        <template v-slot:[`item.created_at`]="{ item }">
          <p>{{ item.created_at }}</p>
        </template>
        <!-- End:: Submission Date -->

        <!-- Start:: Phone Number -->
        <template v-slot:[`item.user.mobile`]="{ item }">
          <p dir="ltr">{{ item.user.mobile }}</p>
        </template>
        <!-- End:: Phone Number -->

        <!-- Start:: Email -->
        <template v-slot:[`item.user.email`]="{ item }">
          <p>{{ item.user.email }}</p>
        </template>
        <!-- End:: Email -->

        <!-- Start:: Status -->
        <template v-slot:[`item.status`]="{ item }">
          <span
            class="status_badge"
            :class="statusClass(item.status)"
          >
            {{ item.status_trans }}
          </span>
        </template>
        <!-- End:: Status -->

        <!-- Start:: Actions -->
        <template v-slot:[`item.actions`]="{ item }">
          <div class="actions">
            <button
              class="btn_show"
              @click="viewComplaintDetails(item)"
              v-if="$can('complaints index', 'complaints')"
            >
              <i class="fal fa-eye"></i>
            </button>
          </div>
        </template>
        <!-- End:: Actions -->
      </v-data-table>
      <!--  =========== End:: Data Table =========== -->

      <!--  =========== Start:: Pagination =========== -->
      <template>
        <div class="pagination_container text-center mt-3 mb-0">
          <v-pagination
            v-model="paginations.current_page"
            :length="paginations.last_page"
            :total-visible="6"
            @input="updateRouterQueryParam($event)"
            :prev-icon="
              getAppLocale == 'ar' ? 'fal fa-angle-right' : 'fal fa-angle-left'
            "
            :next-icon="
              getAppLocale == 'ar' ? 'fal fa-angle-left' : 'fal fa-angle-right'
            "
          ></v-pagination>
        </div>
      </template>
      <!--  =========== End:: Pagination =========== -->
    </main>
    <!-- End:: Main Section -->

    <!-- Start:: Complaint Detail Modal -->
    <ComplaintDetail
      :complaintData="selectedComplaint"
      :showModal="showDetailModal"
      @closeModal="showDetailModal = false"
      @refreshData="getAllComplaints"
    />
    <!-- End:: Complaint Detail Modal -->
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import ComplaintDetail from "./ComplaintDetail.vue";

export default {
  name: "ComplaintsList",

  components: {
    ComplaintDetail,
  },

  data() {
    return {
      // Start:: Loader Control Data
      loading: false,
      isWaitingRequest: false,
      // End:: Loader Control Data

      // Start:: Filter Data
      filterFormIsActive: false,
      filterOptions: {
        q: null,
        status: null,
        booking_id: null,
      },
      // End:: Filter Data

      // Start:: Table Data
      searchValue: "",
      tableHeaders: [
        {
          text: this.$t("complaints.serialNumber"),
          value: "serialNumber",
          align: "center",
          width: "80",
          sortable: false,
        },
        {
          text: this.$t("complaints.complaintId"),
          value: "id",
          align: "center",
          // width: "90",
          sortable: false,
        },
        {
          text: this.$t("complaints.clientName"),
          value: "user.name",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("complaints.submissionDate"),
          value: "created_at",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("complaints.phoneNumber"),
          value: "user.mobile",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("complaints.email"),
          value: "user.email",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("complaints.status"),
          value: "status",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("complaints.actions"),
          value: "actions",
          sortable: false,
          align: "center",
        },
      ],
      tableRows: [],
      // End:: Table Data

      // Start:: Pagination Data
      paginations: {
        current_page: 1,
        last_page: 1,
        items_per_page: 10,
      },
      // End:: Pagination Data

      // Start:: Status Options
      statusOptions: [
        {
          id: null,
          name: this.$t("complaints.allStatuses"),
        },
        {
          id: "open",
          name: this.$t("complaints.open"),
        },
        {
          id: "responded",
          name: this.$t("complaints.responded"),
        },
        {
          id: "resolved",
          name: this.$t("complaints.resolved"),
        },
        {
          id: "rejected",
          name: this.$t("complaints.rejected"),
        },
      ],
      // End:: Status Options

      // Start:: Detail Modal Data
      showDetailModal: false,
      selectedComplaint: null,
      // End:: Detail Modal Data
    };
  },

  computed: {
    ...mapGetters({
      getAppLocale: "AppLangModule/getAppLocale",
    }),
  },

  watch: {
    // Start:: Page Query Param Watcher
    ["$route.query.page"]() {
      this.paginations.current_page = +this.$route.query.page;
      this.getAllComplaints();
    },
    // End:: Page Query Param Watcher
  },

  methods: {
    statusClass(status) {
      return {
        "text-danger": status === "open",
        "text-warning": status === "responded",
        "text-success": status === "resolved" || status === "done" || status === "closed",
        "text-muted": status === "rejected",
      };
    },
    // Start:: Get All Complaints
    async getAllComplaints() {
      this.loading = true;
      try {
        let res = await this.$axios({
          method: "GET",
          url: "complaints",
          params: {
            page: this.paginations.current_page,
            name: this.filterOptions.q,
            status: this.filterOptions.status?.id,
            booking_id: this.filterOptions.booking_id || undefined,
          },
        });
        this.loading = false;
        this.tableRows = res.data.data.data;
        this.paginations.last_page = res.data.data.meta.last_page;
        this.paginations.items_per_page = res.data.data.meta.per_page;
        this.tableRows.forEach((item, index) => {
          item.serialNumber =
            (this.paginations.current_page - 1) *
              this.paginations.items_per_page +
            index +
            1;
        });
      } catch (error) {
        this.loading = false;
        console.log(error.response.data.message);
      }
    },
    // End:: Get All Complaints

    // Start:: Submit Filter Form
    submitFilterForm() {
      this.paginations.current_page = 1;
      this.getAllComplaints();
    },
    // End:: Submit Filter Form

    // Start:: Reset Filter
    resetFilter() {
      this.filterOptions.q = null;
      this.filterOptions.status = null;
      this.filterOptions.booking_id = null;
      this.paginations.current_page = 1;
      this.getAllComplaints();
    },
    // End:: Reset Filter

    // Start:: View Complaint Details
    viewComplaintDetails(complaint) {
      this.selectedComplaint = complaint;
      this.showDetailModal = true;
    },
    // End:: View Complaint Details

    // Start:: Update Router Query Param
    updateRouterQueryParam(pagenationValue) {
      this.$router.push({
        query: {
          ...this.$route.query,
          page: pagenationValue,
        },
      });

      // Scroll To Screen's Top After Get Products
      document.body.scrollTop = 0; // For Safari
      document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
    },
    // End:: Update Router Query Param
  },

  created() {
    // Start:: Fire Methods
    this.paginations.current_page = Number(this.$route.query.page) || 1;
    this.getAllComplaints();
    // End:: Fire Methods
  },
};
</script>

<style scoped lang="scss">
.status_badge {
  padding: 4px 12px;
  border-radius: 4px;
  font-weight: 500;
  font-size: 13px;
}
</style>
